import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        await this.#flyToCart(submitter);
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  /**
   * Fly-to-cart: a thumbnail of the added item arcs from the add button (or the
   * card photo) into the header cart icon, which then bounces. Skipped entirely
   * for reduced-motion users or when the cart icon can't be found.
   */
  async #flyToCart(submitter) {
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Inside the open cart drawer the header icon is covered, so skip the flight.
      if (this.closest('dialog[open]')) return;
      const cart = document.getElementById('header-cart-trigger');
      if (!cart) return;

      const cardImage = this.closest('li, article')?.querySelector('img[data-quick-image]');
      const src = this.querySelector('[data-fly-src]')?.dataset.flySrc || cardImage?.currentSrc || cardImage?.src;
      if (!src) return;

      const startEl = cardImage && this.#inViewport(cardImage) ? cardImage : submitter instanceof HTMLElement ? submitter : this;
      const start = startEl.getBoundingClientRect();
      const end = cart.getBoundingClientRect();
      const size = 96;
      const fromX = start.left + start.width / 2 - size / 2;
      const fromY = start.top + start.height / 2 - size / 2;
      const endVisible = end.bottom > 0 && end.top < window.innerHeight;
      const toX = endVisible ? end.left + end.width / 2 - size / 2 : window.innerWidth - size - 8;
      const toY = endVisible ? end.top + end.height / 2 - size / 2 : -size / 2;
      // Curved path: a cubic Bézier that rises out of the button first, then
      // sweeps sideways and drops into the cart, instead of a straight line.
      const dx = toX - fromX;
      const lift = Math.min(220, Math.max(90, Math.abs(fromY - toY) * 0.45));
      const p0 = { x: fromX, y: fromY };
      const p1 = { x: fromX - Math.sign(dx || 1) * 60, y: Math.max(8, Math.min(fromY - lift, (fromY + toY) / 2)) };
      const p2 = { x: toX - Math.sign(dx || 1) * Math.max(140, Math.abs(dx) * 0.35), y: Math.max(8, toY + lift * 0.25) };
      const p3 = { x: toX, y: toY };
      const bez = (a, b, c, d, t) => {
        const u = 1 - t;
        return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d;
      };
      const bezD = (a, b, c, d, t) => {
        const u = 1 - t;
        return 3 * u * u * (b - a) + 6 * u * t * (c - b) + 3 * t * t * (d - c);
      };
      const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      const flyer = document.createElement('img');
      flyer.src = src;
      flyer.alt = '';
      flyer.setAttribute('aria-hidden', 'true');
      Object.assign(flyer.style, {
        position: 'fixed',
        left: '0',
        top: '0',
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'cover',
        borderRadius: '50%',
        background: 'var(--color-surface)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: '2147483000',
        pointerEvents: 'none',
        willChange: 'transform, opacity, border-radius',
      });
      document.body.append(flyer);

      // Liquid feel: the photo stretches along its direction of travel, squashes
      // as it slows, and its outline wobbles like a blob of liquid.
      const steps = 48;
      const frames = [];
      let maxSpeed = 1;
      const samples = [];
      for (let i = 0; i <= steps; i++) {
        const time = i / steps;
        const t = easeInOut(time);
        const vx = bezD(p0.x, p1.x, p2.x, p3.x, t);
        const vy = bezD(p0.y, p1.y, p2.y, p3.y, t);
        const dt = i === 0 || i === steps ? 0 : (easeInOut(Math.min(1, time + 0.01)) - easeInOut(Math.max(0, time - 0.01))) / 0.02;
        const speed = Math.hypot(vx, vy) * dt;
        maxSpeed = Math.max(maxSpeed, speed);
        samples.push({ time, t, vx, vy, speed });
      }
      for (const s of samples) {
        const x = bez(p0.x, p1.x, p2.x, p3.x, s.t);
        const y = bez(p0.y, p1.y, p2.y, p3.y, s.t);
        const angle = Math.atan2(s.vy, s.vx) * (180 / Math.PI);
        const stretch = 1 + 0.45 * (s.speed / maxSpeed);
        const squash = 1 / Math.sqrt(stretch);
        // Pop out with a gloopy overshoot, then shrink as it lands in the cart.
        const pop = s.time < 0.15 ? 0.6 + 0.5 * Math.sin((s.time / 0.15) * (Math.PI / 2)) : 1.1 - 0.1 * Math.min(1, (s.time - 0.15) / 0.1);
        const shrink = s.time > 0.7 ? 1 - 0.75 * ((s.time - 0.7) / 0.3) : 1;
        const scale = pop * shrink;
        const w = s.time * Math.PI * 6;
        const r = (base, phase) => Math.round(base + 12 * Math.sin(w + phase));
        frames.push({
          transform: `translate(${x}px, ${y}px) rotate(${angle}deg) scale(${stretch}, ${squash}) rotate(${-angle}deg) scale(${scale})`,
          borderRadius: `${r(50, 0)}% ${r(50, 1.7)}% ${r(50, 3.1)}% ${r(50, 4.6)}% / ${r(50, 2.3)}% ${r(50, 0.8)}% ${r(50, 5.2)}% ${r(50, 3.9)}%`,
          opacity: s.time > 0.9 ? 1 - ((s.time - 0.9) / 0.1) * 0.4 : 1,
          offset: s.time,
        });
      }

      const flight = flyer.animate(frames, { duration: 1200, easing: 'linear', fill: 'forwards' });
      await flight.finished;
      flyer.remove();

      // Jelly wobble on the cart icon as the item "splashes" in.
      const icon = cart.querySelector('span') || cart;
      await icon.animate(
        [
          { transform: 'scale(1, 1)' },
          { transform: 'scale(1.35, 0.75)', offset: 0.2 },
          { transform: 'scale(0.8, 1.25)', offset: 0.4 },
          { transform: 'scale(1.12, 0.9)', offset: 0.6 },
          { transform: 'scale(0.96, 1.05)', offset: 0.8 },
          { transform: 'scale(1, 1)' },
        ],
        { duration: 650, easing: 'ease-out' }
      ).finished;
    } catch {
      // Animation is decorative; never block opening the cart.
    }
  }

  #inViewport(el) {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight;
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
